import React from 'react'
import PropTypes from 'prop-types'
import {
    Badge,
    useMultiStyleConfig,
    VStack,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGlobalBadges} from '../../contexts/global-elements-context'
import {getHideStyles} from '../optimizely/utils/utils'
import {shouldHideSaleBadgeForLocale} from '../../utils/sale-badges'

const Badges = ({badges = [], badgesContainerStyleProps = {}, badgesStylesProps = {}}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const styles = useMultiStyleConfig('ProductTile', {isDesktop})
    const {data: globalBadges} = useGlobalBadges()
    const {locale} = useMultiSite()

    // Get the badge from the product
    const badge = Array.isArray(badges) ? badges[0] : badges?.badges?.[0]

    if (!badge) return null

    const {id} = badge

    // Map badge IDs from product format (camelCase) to configuration format (snake_case)
    const badgeIdMap = {
        isBestseller: 'grg_badge_bestseller',
        isAlmostGone: 'grg_badge_almost_gone',
        isUKSale: 'grg_badge_uk_sale',
        isUSSale: 'grg_badge_us_sale',
        isCASale: 'grg_badge_ca_sale'
    }

    // Get the configuration ID, using the mapping if available
    const configId = badgeIdMap[id] || id

    if (shouldHideSaleBadgeForLocale(locale, configId)) {
        return null
    }

    // Find the corresponding badge configuration from Optimizely
    const badgeConfig = globalBadges?.find((b) => b.id === configId)

    // If no config found in Optimizely, don't render the badge
    if (!badgeConfig) {
        return null
    }

    // Extract styles from Optimizely data
    const {text, textStyle, backgroundColor, opacity, width, height, borderRadius, HideOnDevices} =
        badgeConfig

    if (shouldHideSaleBadgeForLocale(locale, {id: configId, text})) {
        return null
    }

    // Convert opacity percentage to decimal for rgba
    const bgColor = backgroundColor
        ? `${backgroundColor}${Math.round((opacity / 100) * 255)
              .toString(16)
              .padStart(2, '0')}`
        : 'rgba(0, 0, 0, 0.7)'

    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return

    return (
        <VStack {...styles.badgeGroup} {...badgesContainerStyleProps} {...hideStyles}>
            <Badge
                {...styles.badge}
                key={id}
                data-testid="product-badge"
                bg={bgColor}
                color={textStyle?.color?.base || 'white'}
                fontFamily={textStyle?.fontFamily?.base}
                fontWeight={textStyle?.fontWeight?.base}
                fontSize={textStyle?.fontSize?.base}
                lineHeight="18px"
                width={width ? `${width}px` : 'auto'}
                height={height ? `${height}px` : 'auto'}
                borderRadius={borderRadius ? `${borderRadius}px` : '0'}
                {...badgesStylesProps}
            >
                {text || id}
            </Badge>
        </VStack>
    )
}

Badges.propTypes = {
    badges: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                contentId: PropTypes.object
            })
        ),
        PropTypes.shape({
            badges: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    contentId: PropTypes.object
                })
            )
        })
    ]),
    badgesContainerStyleProps: PropTypes.object,
    badgesStylesProps: PropTypes.object
}

export default Badges
